import { useTheme } from "@sundae/react-hooks";
import { useMemo } from "react";
import { TasteTest } from "../../gql/generated/taste-test.sdk";
import { useHandleData, usePersonalizedHandleData } from "../../gql/hooks/handle.query";
import { getIpfsImagePath } from "../../utils/images.utils";

const HANDLE_TEXT_SHADOW_STEP = 0.22; // 1 Step (i.e. for text-shadow) in the Handle Designer equals ~0.22; So setting a text shadow with a horizontal value of `20` in the Handle Designer would be `4.4px` for css.

export interface IUseTasteTestPersonalization {
  backgroundColor?: string;
  bannerImage?: string;
  pfpImage?: string;
  primaryColor?: string;
  ribbonColor?: string;
  textShadowColor?: string;
  textShadowSize?: string;
}

export const useTasteTestPersonalization = (tasteTest: TasteTest | undefined) => {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const defaultBannerImage = `/static/images/banners/banner_design_taste_test${
    isLight ? "_light" : ""
  }.png`;
  const { data: handleData, isLoading: isLoadingHandleData } = useHandleData(
    tasteTest?.adaHandle ?? ""
  );
  const { data: personalizedHandleData, isLoading: isLoadingPersonalizedHandleData } =
    usePersonalizedHandleData(tasteTest?.adaHandle ?? "");

  const transformHandleColor = (color: string | undefined) => {
    return color ? color.replace("0x", "#") : undefined;
  };

  const bannerImage = handleData?.bg_image
    ? getIpfsImagePath(handleData?.bg_image)
    : defaultBannerImage;
  const pfpImage =
    tasteTest?.asset?.logo ||
    handleData?.pfp_image ||
    "/static/images/pfp/default_avatar_taste_test_v2.png";

  const tasteTestPersonalization: IUseTasteTestPersonalization = useMemo(() => {
    return {
      backgroundColor: transformHandleColor(personalizedHandleData?.bg_color),
      bannerImage,
      pfpImage,
      primaryColor: transformHandleColor(personalizedHandleData?.bg_border_color),
      ribbonColor: transformHandleColor(personalizedHandleData?.text_ribbon_colors?.[0]),
      textShadowColor: transformHandleColor(personalizedHandleData?.font_shadow_color),
      textShadowSize: personalizedHandleData?.font_shadow_size
        ? `${personalizedHandleData.font_shadow_size[0] * HANDLE_TEXT_SHADOW_STEP}px ${
            personalizedHandleData.font_shadow_size[1] * HANDLE_TEXT_SHADOW_STEP
          }px ${personalizedHandleData.font_shadow_size[2] * HANDLE_TEXT_SHADOW_STEP}px`
        : "0px",
    };
  }, [defaultBannerImage, personalizedHandleData, handleData, tasteTest?.asset?.logo]);

  return {
    isLoading: isLoadingHandleData || isLoadingPersonalizedHandleData,
    tasteTestPersonalization,
  };
};
